import { useCallback, useEffect, useMemo, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import LiftPDFDocument from "./LiftPDFDocument";

const buttonClass = "pd-btn pd-btn--ghost";
const fileName = "pebbledash-lift-report.pdf";

const serialize = (value) => {
  if (value === undefined) return null;
  try {
    // This is the most robust way to ensure we have a plain JS object
    // with no hidden prototypes, functions, or undefined values
    return JSON.parse(JSON.stringify(value));
  } catch (err) {
    console.warn("[lifts] Failed to serialise data for PDF export", err);
    return null;
  }
};

export default function DownloadLiftReportBtn({ project, inputs, result, disabled }) {
  const normalizeSimulation = useCallback((value) => {
    if (!value) return null;
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch (err) {
        console.warn("[lifts] Failed to parse saved simulation", err);
        return null;
      }
    }
    return typeof value === "object" ? value : null;
  }, []);

  const exportableResult = useMemo(
    () => normalizeSimulation(result?.response) || normalizeSimulation(result),
    [normalizeSimulation, result],
  );

  const exportableInputs = useMemo(() => {
    if (inputs) return inputs;
    if (result?.floorMeta) return result.floorMeta;
    return null;
  }, [inputs, result]);

  const serializedProject = useMemo(() => serialize(project), [project]);
  const serializedInputs = useMemo(() => serialize(exportableInputs), [exportableInputs]);
  const serializedResult = useMemo(() => serialize(exportableResult), [exportableResult]);

  const plainProject = useMemo(() => ({ ...(serializedProject || {}) }), [serializedProject]);
  const plainInputs = useMemo(() => ({ ...(serializedInputs || {}) }), [serializedInputs]);
  const plainResult = useMemo(() => ({ ...(serializedResult || {}) }), [serializedResult]);

  const serializationError = useMemo(() => {
    if (project && !serializedProject) {
      return "Export unavailable: failed to prepare project details.";
    }
    if (exportableInputs && !serializedInputs) {
      return "Export unavailable: failed to prepare lift inputs.";
    }
    if (exportableResult && !serializedResult) {
      return "Export unavailable: failed to prepare simulation results.";
    }
    return "";
  }, [exportableInputs, exportableResult, project, serializedInputs, serializedProject, serializedResult]);

  const hasResult = useMemo(
    () => Boolean(exportableResult && typeof exportableResult === "object"),
    [exportableResult],
  );
  const simulationPayload = useMemo(() => {
    if (!serializedResult || typeof serializedResult !== "object") return null;
    if (serializedResult.response && typeof serializedResult.response === "object") {
      return serializedResult.response;
    }
    return serializedResult;
  }, [serializedResult]);

  const hasExpectedArrays = useMemo(
    () => Array.isArray(simulationPayload?.all_results),
    [simulationPayload],
  );

  const exportReady = useMemo(() => {
    const hasSerializedData =
      serializedProject && serializedInputs && serializedResult && simulationPayload;
    const hasObjects =
      typeof serializedProject === "object" &&
      typeof serializedInputs === "object" &&
      typeof simulationPayload === "object";

    return Boolean(hasSerializedData && hasObjects && hasExpectedArrays && !serializationError);
  }, [
    hasExpectedArrays,
    serializationError,
    serializedInputs,
    serializedProject,
    serializedResult,
    simulationPayload,
  ]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (exportReady) {
      setError("");
    }
  }, [exportReady]);

  useEffect(() => {
    if (!hasResult) return;
    if (serializationError) {
      setError(serializationError);
      return;
    }
    if (!hasExpectedArrays) {
      setError("Export unavailable: results are incomplete.");
    }
  }, [hasExpectedArrays, hasResult, serializationError]);

  const handleDownload = useCallback(async () => {
    if (!hasResult || disabled || isGenerating) return;
    if (serializationError) {
      setError(serializationError);
      return;
    }
    if (!exportReady) {
      const message = !hasExpectedArrays
        ? "Export unavailable: results are incomplete."
        : "Export unavailable: data is not ready.";
      setError(message);
      return;
    }

    setError("");
    setIsGenerating(true);

    try {
      const blob = await pdf(
        <LiftPDFDocument project={plainProject} inputs={plainInputs} result={plainResult} />,
        {},
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[lifts] PDF export failed", err);
      const message = err?.message || "Export unavailable";
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  }, [
    disabled,
    hasExpectedArrays,
    exportReady,
    hasResult,
    isGenerating,
    project,
    serializedInputs,
    serializedProject,
    serializedResult,
    serializationError,
  ]);

  return (
    <button
      type="button"
      className={buttonClass}
      onClick={handleDownload}
      disabled={disabled || !hasResult || !exportReady || isGenerating}
      aria-disabled={disabled || !hasResult || !exportReady || isGenerating}
      title={error || undefined}
    >
      {isGenerating ? "Generating…" : "Export report"}
    </button>
  );
}
